// src/pages/AdvisorDashboard.js
import React, { useMemo, useState } from "react";
import { useROStore } from "../context/ROContext";
import { RO_STATUS, RO_LIFECYCLE } from "../constants/roStatus";
import { normalizeROArray } from "../utils/metricsEngine";
import {
  isAdvisorDashboardActiveRO,
  isAdvisorDashboardCompletedRO,
  isCompletedLikeRO,
  isVehicleGoneLikeRO,
} from "../utils/roDashboardStatus";

/* ===============================
   Helpers
================================ */
function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toTime(input) {
  const d = safeDate(input);
  return d ? d.getTime() : null;
}

function formatDateTime(input) {
  const d = safeDate(input);
  if (!d) return "—";

  return `${d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function formatHours(value) {
  const n = safeNumber(value);
  if (!n) return "0.0";
  return n.toFixed(1);
}

function normalizeName(v) {
  return safeString(v).trim().toLowerCase();
}

function advisorDisplayName(ro) {
  return (
    safeString(
      ro?.advisorName || ro?.serviceAdvisorName || ro?.writerName || ""
    ).trim() || "—"
  );
}

function isArchivedLike(ro) {
  const status = safeString(ro?.status).toUpperCase();
  const lifecycle = safeString(ro?.lifecycle || ro?.lifeCycle).toUpperCase();

  return (
    status === safeString(RO_STATUS.ARCHIVED).toUpperCase() ||
    status === "ARCHIVED" ||
    lifecycle === "ARCHIVED"
  );
}

function isVehicleGoneLike(ro) {
  return isVehicleGoneLikeRO(ro);
}

function normalizeLifecycle(v) {
  const s = safeString(v).toUpperCase();

  if (s === "WAITING_ON_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_PARTS") return RO_LIFECYCLE.WAITING_FOR_PARTS;
  if (s === "WAITING_FOR_ASSIGNMENT") return RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT;
  if (s === "IN_PROGRESS") return RO_LIFECYCLE.IN_PROGRESS;
  if (s === "COMPLETED") return RO_LIFECYCLE.COMPLETED;
  if (s === "OPEN") return RO_LIFECYCLE.OPEN;

  return s || RO_LIFECYCLE.OPEN;
}

function isCompletedLike(ro) {
  return isCompletedLikeRO(ro);
}

function isOpenLike(ro) {
  return !isArchivedLike(ro) && !isVehicleGoneLike(ro) && !isCompletedLike(ro);
}

function isWaitingParts(ro) {
  const lifecycle = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);
  const status = safeString(ro?.status).toUpperCase();

  return (
    lifecycle === RO_LIFECYCLE.WAITING_FOR_PARTS ||
    status === "WAITING_ON_PARTS" ||
    status === "WAITING_FOR_PARTS"
  );
}

function isInProgress(ro) {
  const lifecycle = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);
  const status = safeString(ro?.status).toUpperCase();

  return lifecycle === RO_LIFECYCLE.IN_PROGRESS || status === "IN_PROGRESS";
}

function isUnassigned(ro) {
  const lifecycle = normalizeLifecycle(ro?.lifecycle || ro?.lifeCycle);
  const hasTech =
    !!safeString(ro?.assignedTechId).trim() ||
    !!safeString(ro?.assignedTechName).trim();

  return lifecycle === RO_LIFECYCLE.WAITING_FOR_ASSIGNMENT || !hasTech;
}

function isWaiter(ro) {
  return safeString(ro?.transportType).toUpperCase() === "WAITER";
}

function isPromiseRisk(ro) {
  const open = isOpenLike(ro);
  if (!open) return false;

  const promiseMs = toTime(ro?.promiseTimeISO || ro?.promiseTime);
  if (!promiseMs) return false;

  const now = Date.now();
  const oneHour = 60 * 60 * 1000;

  return promiseMs <= now + oneHour;
}

function isOverdue(ro) {
  const open = isOpenLike(ro);
  if (!open) return false;

  const promiseMs = toTime(ro?.promiseTimeISO || ro?.promiseTime);
  if (!promiseMs) return false;

  return promiseMs < Date.now();
}

const DATE_FILTERS = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function startOfLocalDay(input = new Date()) {
  const d = safeDate(input) || new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(input, days) {
  const d = new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = startOfLocalDay(input);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function dateInputValue(input = new Date()) {
  const d = safeDate(input) || new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function localDateFromInput(value) {
  const s = safeString(value).trim();
  if (!s) return null;

  const [year, month, day] = s.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function getAdvisorDateWindow(filter, customFrom, customTo) {
  const todayStart = startOfLocalDay();
  const tomorrowStart = addDays(todayStart, 1);

  if (filter === DATE_FILTERS.YESTERDAY) {
    return {
      start: addDays(todayStart, -1),
      end: todayStart,
      label: "Yesterday",
    };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      start: startOfLocalWeekMonday(),
      end: tomorrowStart,
      label: "This Week",
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: tomorrowStart,
      label: "This Month",
    };
  }

  if (filter === DATE_FILTERS.CUSTOM) {
    const start = localDateFromInput(customFrom);
    const toStart = localDateFromInput(customTo);
    const end = toStart ? addDays(toStart, 1) : null;

    return {
      start,
      end,
      label:
        customFrom && customTo
          ? `${customFrom} to ${customTo}`
          : "Custom Range",
    };
  }

  return {
    start: todayStart,
    end: tomorrowStart,
    label: "Today",
  };
}

function isWithinDateWindow(input, dateWindow) {
  const t = toTime(input);
  const start = dateWindow?.start?.getTime?.();
  const end = dateWindow?.end?.getTime?.();

  if (!Number.isFinite(t)) return false;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return true;

  return t >= start && t < end;
}

function getCompletedAt(ro) {
  return (
    ro?.timestamps?.completedAt ||
    ro?.timestamps?.completed_at ||
    ro?.timestamps?.technicianCompletedAt ||
    ro?.completedAtISO ||
    ro?.completed_at_iso ||
    ro?.completedAt ||
    ro?.completed_at ||
    ro?.technicianCompletedAtISO ||
    ro?.technician_completed_at_iso ||
    ro?.closedAt ||
    ro?.closed_at ||
    ro?.updatedAtISO ||
    ro?.updatedAt ||
    ""
  );
}

function getCreatedAt(ro) {
  return (
    ro?.timestamps?.createdAt ||
    ro?.timestamps?.openedAt ||
    ro?.timestamps?.inAt ||
    ro?.timestamps?.arrivedAt ||
    ro?.createdAtISO ||
    ro?.createdAtIso ||
    ro?.createdAtUtc ||
    ro?.createdAt ||
    ro?.openedAtISO ||
    ro?.openedAt ||
    ro?.inAt ||
    ro?.arrivedAt ||
    ""
  );
}

function getHoursSold(ro) {
  return (
    safeNumber(ro?.hoursSold) ||
    safeNumber(ro?.soldHours) ||
    safeNumber(ro?.flaggedHours) ||
    safeNumber(ro?.laborHoursSold) ||
    safeNumber(ro?.totalHoursSold)
  );
}

function getHoursCompleted(ro) {
  return (
    safeNumber(ro?.hoursCompleted) ||
    safeNumber(ro?.completedHours) ||
    safeNumber(ro?.techCompletedHours) ||
    safeNumber(ro?.flaggedHoursCompleted) ||
    safeNumber(ro?.flaggedHours)
  );
}

function workMixLabel(v) {
  const s = safeString(v).toUpperCase();
  if (s === "MAINTENANCE") return "Maintenance";
  if (s === "DIAGNOSIS") return "Diagnosis";
  return "Other";
}

function transportLabel(v) {
  const s = safeString(v).toUpperCase();
  if (s === "WAITER") return "Waiter";
  if (s === "LOANER") return "Loaner";
  if (s === "SHUTTLE") return "Shuttle";
  if (s === "OTHER") return "Other";
  return "Other";
}

function percent(numerator, denominator) {
  const n = safeNumber(numerator);
  const d = safeNumber(denominator);
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

/* ===============================
   Styles
================================ */
const page = {
  padding: 12,
  display: "grid",
  gap: 10,
  maxWidth: 1500,
  margin: "0 auto",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};

const eyebrow = {
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(15,23,42,0.52)",
  marginBottom: 3,
};

const title = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1.02,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  color: "rgba(15,23,42,0.94)",
};

const subtitle = {
  margin: "4px 0 0",
  fontSize: 11.5,
  color: "rgba(15,23,42,0.58)",
  lineHeight: 1.32,
};

const card = {
  background: "#ffffff",
  border: "1px solid rgba(15,23,42,0.08)",
  borderRadius: 14,
  padding: 11,
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
};

const metricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 8,
};

const metricLabel = {
  fontSize: 10.2,
  fontWeight: 850,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "rgba(15,23,42,0.48)",
};

const metricValue = {
  marginTop: 3,
  fontSize: 20,
  lineHeight: 1,
  fontWeight: 800,
  color: "rgba(15,23,42,0.92)",
  letterSpacing: "-0.025em",
};

const metricSub = {
  marginTop: 5,
  fontSize: 10.8,
  color: "rgba(15,23,42,0.55)",
  lineHeight: 1.28,
};

const sectionTitle = {
  fontSize: 10.5,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "rgba(15,23,42,0.72)",
  marginBottom: 7,
};

const smallSelect = {
  height: 34,
  padding: "0 10px",
  borderRadius: 9,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "#ffffff",
  color: "rgba(15,23,42,0.90)",
  fontSize: 12.2,
  fontWeight: 650,
  fontFamily: "inherit",
  minWidth: 210,
  outline: "none",
};

function metricCardStyle(tone = "default") {
  let borderColor = "#e5e7eb";
  let background = "#ffffff";

  if (tone === "danger") {
    borderColor = "#fecaca";
    background = "#fffafa";
  }

  if (tone === "warn") {
    borderColor = "#fed7aa";
    background = "#fff7ed";
  }

  if (tone === "good") {
    borderColor = "#bbf7d0";
    background = "#f0fdf4";
  }

  if (tone === "blue") {
    borderColor = "#bfdbfe";
    background = "#eff6ff";
  }

  return {
    ...card,
    borderColor,
    background,
    minHeight: 62,
  };
}

function pillStyle(tone = "default") {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.94)",
    color: "rgba(15,23,42,0.68)",
    fontSize: 10.5,
    fontWeight: 900,
    whiteSpace: "nowrap",
  };

  if (tone === "danger") {
    return {
      ...base,
      borderColor: "rgba(180,35,24,0.14)",
      background: "rgba(254,243,242,0.96)",
      color: "#b42318",
    };
  }

  if (tone === "warn") {
    return {
      ...base,
      borderColor: "rgba(181,71,8,0.14)",
      background: "rgba(255,247,237,0.94)",
      color: "#b54708",
    };
  }

  if (tone === "good") {
    return {
      ...base,
      borderColor: "rgba(6,118,71,0.14)",
      background: "rgba(236,253,243,0.94)",
      color: "#067647",
    };
  }

  if (tone === "blue") {
    return {
      ...base,
      borderColor: "rgba(37,99,235,0.14)",
      background: "rgba(239,246,255,0.94)",
      color: "#1d4ed8",
    };
  }

  return base;
}

function ProgressBar({ value }) {
  const pct = Math.max(0, Math.min(100, safeNumber(value)));

  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        background: "#eef2f7",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          borderRadius: 999,
          background: "#0f172a",
        }}
      />
    </div>
  );
}

/* ===============================
   Component
================================ */
export default function AdvisorDashboard() {
  const store = useROStore();
  const ros = useMemo(() => normalizeROArray(store), [store]);

  /*
    Advisor Dashboard should use the same corrected active WIP source
    as Advisor Log / WIP Board for open workload metrics. Do not calculate
    open advisor work from the full raw RO list, because old picked-up or
    hidden records can inflate the advisor metrics.
  */
  const activeAdvisorSourceROs = useMemo(() => {
    const source = Array.isArray(store?.advisorActiveROs)
      ? store.advisorActiveROs
      : Array.isArray(store?.visibleBoardROs)
      ? store.visibleBoardROs
      : Array.isArray(store?.visibleWipROs)
      ? store.visibleWipROs
      : Array.isArray(store?.wipROs)
      ? store.wipROs
      : Array.isArray(store?.openROs)
      ? store.openROs
      : ros.filter(isOpenLike);

    return source.filter((ro) => !isArchivedLike(ro));
  }, [store, ros]);

  const completedAdvisorSourceROs = useMemo(() => {
    const source = Array.isArray(store?.completedROs)
      ? store.completedROs
      : ros.filter(isCompletedLike);

    return source.filter((ro) => !isArchivedLike(ro));
  }, [store, ros]);

  const currentUser = store?.currentUser || {};
  const currentAdvisorName = safeString(currentUser?.name).trim();

  const advisorOptions = useMemo(() => {
    const names = new Set();

    ros.forEach((ro) => {
      const name = advisorDisplayName(ro);
      if (name && name !== "—") names.add(name);
    });

    if (currentAdvisorName) names.add(currentAdvisorName);

    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [ros, currentAdvisorName]);

  const [selectedAdvisor, setSelectedAdvisor] = useState(
    currentAdvisorName || "All Advisors"
  );
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.TODAY);
  const [customFromDate, setCustomFromDate] = useState(() => dateInputValue());
  const [customToDate, setCustomToDate] = useState(() => dateInputValue());

  const dateWindow = useMemo(
    () => getAdvisorDateWindow(dateFilter, customFromDate, customToDate),
    [dateFilter, customFromDate, customToDate]
  );

  const advisorScope = useMemo(() => {
    if (!selectedAdvisor || selectedAdvisor === "All Advisors") {
      return activeAdvisorSourceROs.filter(isAdvisorDashboardActiveRO);
    }

    const selected = normalizeName(selectedAdvisor);

    return activeAdvisorSourceROs.filter((ro) => {
      if (!isAdvisorDashboardActiveRO(ro)) return false;
      return normalizeName(advisorDisplayName(ro)) === selected;
    });
  }, [activeAdvisorSourceROs, selectedAdvisor]);

  const completedAdvisorScope = useMemo(() => {
    const selected = normalizeName(selectedAdvisor);

    return completedAdvisorSourceROs.filter((ro) => {
      if (!isAdvisorDashboardCompletedRO(ro)) return false;
      if (!selectedAdvisor || selectedAdvisor === "All Advisors") return true;
      return normalizeName(advisorDisplayName(ro)) === selected;
    });
  }, [completedAdvisorSourceROs, selectedAdvisor]);

  const createdAdvisorScope = useMemo(() => {
    const selected = normalizeName(selectedAdvisor);

    return ros.filter((ro) => {
      if (isArchivedLike(ro)) return false;
      if (!selectedAdvisor || selectedAdvisor === "All Advisors") return true;
      return normalizeName(advisorDisplayName(ro)) === selected;
    });
  }, [ros, selectedAdvisor]);

  const metrics = useMemo(() => {
    const open = advisorScope.filter((ro) => !isArchivedLike(ro));
    const completed = completedAdvisorScope
      .filter(isCompletedLike)
      .filter((ro) => isWithinDateWindow(getCompletedAt(ro), dateWindow));

    const createdInRange = createdAdvisorScope.filter((ro) =>
      isWithinDateWindow(getCreatedAt(ro), dateWindow)
    );

    const overdue = open.filter(isOverdue);
    const promiseRisk = open.filter(isPromiseRisk);
    const waiters = open.filter(isWaiter);
    const unassigned = open.filter(isUnassigned);
    const waitingParts = open.filter(isWaitingParts);
    const inProgress = open.filter(isInProgress);
    const customerContacted = open.filter((ro) => !!ro?.customerContacted);

    const totalHoursSold = createdInRange.reduce(
      (sum, ro) => sum + getHoursSold(ro),
      0
    );

    const completedHours = completed.reduce(
      (sum, ro) => sum + getHoursCompleted(ro),
      0
    );

    const avgHoursPerCompletedRO = completed.length
      ? completedHours / completed.length
      : 0;

    const avgHoursPerWrittenRO = createdInRange.length
      ? totalHoursSold / createdInRange.length
      : 0;

    const workMixCounts = open.reduce(
      (acc, ro) => {
        const label = workMixLabel(ro?.workMix || ro?.concern);
        acc[label] = (acc[label] || 0) + 1;
        return acc;
      },
      { Maintenance: 0, Diagnosis: 0, Other: 0 }
    );

    const transportCounts = open.reduce(
      (acc, ro) => {
        const label = transportLabel(ro?.transportType);
        acc[label] = (acc[label] || 0) + 1;
        return acc;
      },
      { Waiter: 0, Loaner: 0, Shuttle: 0, Other: 0 }
    );

    const recentlyCompleted = [...completed]
      .sort((a, b) => {
        const aTime = toTime(getCompletedAt(a)) || 0;
        const bTime = toTime(getCompletedAt(b)) || 0;
        return bTime - aTime;
      })
      .slice(0, 6);

    const nextPromises = [...open]
      .filter((ro) => toTime(ro?.promiseTimeISO || ro?.promiseTime))
      .sort((a, b) => {
        const aTime = toTime(a?.promiseTimeISO || a?.promiseTime) || 0;
        const bTime = toTime(b?.promiseTimeISO || b?.promiseTime) || 0;
        return aTime - bTime;
      })
      .slice(0, 6);

    return {
      open,
      completed,
      createdInRange,
      overdue,
      promiseRisk,
      waiters,
      unassigned,
      waitingParts,
      inProgress,
      customerContacted,
      totalHoursSold,
      completedHours,
      avgHoursPerCompletedRO,
      avgHoursPerWrittenRO,
      workMixCounts,
      transportCounts,
      recentlyCompleted,
      nextPromises,
      contactRate: percent(customerContacted.length, open.length),
      completionRate: percent(completed.length, createdInRange.length),
    };
  }, [advisorScope, completedAdvisorScope, createdAdvisorScope, dateWindow]);

  const healthTone =
    metrics.overdue.length > 0
      ? "danger"
      : metrics.promiseRisk.length > 0 || metrics.waiters.length > 0
      ? "warn"
      : "good";

  const healthText =
    metrics.overdue.length > 0
      ? "Needs attention"
      : metrics.promiseRisk.length > 0
      ? "Watch promises"
      : metrics.waiters.length > 0
      ? "Watch waiters"
      : "Stable";

  return (
    <div style={page}>
      <div style={headerRow}>
        <div>
          <div style={eyebrow}>Advisor Performance</div>
          <h1 style={title}>Advisor Dashboard</h1>
          <div style={subtitle}>
            Live advisor view for open workload, promise follow-up, daily RO count,
            and sold hours.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <select
            value={selectedAdvisor}
            onChange={(e) => setSelectedAdvisor(e.target.value)}
            style={smallSelect}
          >
            <option value="All Advisors">All Advisors</option>
            {advisorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{ ...smallSelect, minWidth: 160 }}
          >
            <option value={DATE_FILTERS.TODAY}>Date: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Date: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Date: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Date: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Date: Custom</option>
          </select>

          {dateFilter === DATE_FILTERS.CUSTOM ? (
            <>
              <input
                type="date"
                value={customFromDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={{ ...smallSelect, minWidth: 145 }}
              />

              <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 900 }}>
                to
              </span>

              <input
                type="date"
                value={customToDate}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={{ ...smallSelect, minWidth: 145 }}
              />
            </>
          ) : null}

          <span style={pillStyle(healthTone)}>{healthText}</span>
        </div>
      </div>

      <div style={metricGrid}>
        <div style={metricCardStyle(healthTone)}>
          <div style={metricLabel}>Open ROs</div>
          <div style={metricValue}>{metrics.open.length}</div>
          <div style={metricSub}>
            {metrics.unassigned.length} unassigned • {metrics.inProgress.length} in
            progress
          </div>
        </div>

        <div style={metricCardStyle(metrics.overdue.length ? "danger" : "blue")}>
          <div style={metricLabel}>Promise Risk</div>
          <div style={metricValue}>{metrics.promiseRisk.length}</div>
          <div style={metricSub}>
            {metrics.overdue.length} overdue • {metrics.waiters.length} waiters
          </div>
        </div>

        <div style={metricCardStyle("good")}>
          <div style={metricLabel}>Completed ROs</div>
          <div style={metricValue}>{metrics.completed.length}</div>
          <div style={metricSub}>Completed during {dateWindow.label}.</div>
        </div>

        <div style={metricCardStyle("blue")}>
          <div style={metricLabel}>Hours Completed</div>
          <div style={metricValue}>{formatHours(metrics.completedHours)}</div>
          <div style={metricSub}>
            {dateWindow.label} • Avg {formatHours(metrics.avgHoursPerCompletedRO)} per
            completed RO
          </div>
        </div>
      </div>

      <div style={metricGrid}>
        <div style={metricCardStyle()}>
          <div style={metricLabel}>Total RO Count</div>
          <div style={metricValue}>{metrics.createdInRange.length}</div>
          <div style={metricSub}>
            Entered during {dateWindow.label}.
          </div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Sold / Flagged Hours</div>
          <div style={metricValue}>{formatHours(metrics.totalHoursSold)}</div>
          <div style={metricSub}>
            Avg {formatHours(metrics.avgHoursPerWrittenRO)} per RO written.
          </div>
        </div>

        <div style={metricCardStyle(metrics.waitingParts.length ? "warn" : "good")}>
          <div style={metricLabel}>Waiting Parts</div>
          <div style={metricValue}>{metrics.waitingParts.length}</div>
          <div style={metricSub}>
            Open advisor ROs waiting on parts.
          </div>
        </div>

        <div style={metricCardStyle()}>
          <div style={metricLabel}>Customer Follow-Up</div>
          <div style={metricValue}>{metrics.contactRate}%</div>
          <div style={metricSub}>
            {metrics.customerContacted.length} of {metrics.open.length} open ROs
            marked customer contacted.
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Advisor Summary</div>
        <div style={summaryGrid}>
          <SummaryStat label="Advisor scope" value={selectedAdvisor} />
          <SummaryStat label="Date range" value={dateWindow.label} />
          <SummaryStat label="Total RO count" value={metrics.createdInRange.length} />
          <SummaryStat label="Open ROs" value={metrics.open.length} />
          <SummaryStat label="Open waiters" value={metrics.waiters.length} />
          <SummaryStat label="Overdue promises" value={metrics.overdue.length} />
          <SummaryStat
            label="Avg completed hours / RO"
            value={formatHours(metrics.avgHoursPerCompletedRO)}
            style={{ gridColumn: "span 2" }}
          />
        </div>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Advisor Work Mix</div>

        <div style={{ display: "grid", gap: 7 }}>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11.5,
                marginBottom: 5,
              }}
            >
              <b>Maintenance</b>
              <span>{metrics.workMixCounts.Maintenance || 0}</span>
            </div>
            <ProgressBar
              value={percent(metrics.workMixCounts.Maintenance, metrics.open.length)}
            />
          </div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11.5,
                marginBottom: 5,
              }}
            >
              <b>Diagnosis</b>
              <span>{metrics.workMixCounts.Diagnosis || 0}</span>
            </div>
            <ProgressBar
              value={percent(metrics.workMixCounts.Diagnosis, metrics.open.length)}
            />
          </div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11.5,
                marginBottom: 5,
              }}
            >
              <b>Other</b>
              <span>{metrics.workMixCounts.Other || 0}</span>
            </div>
            <ProgressBar
              value={percent(metrics.workMixCounts.Other, metrics.open.length)}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: 9,
            }}
          >
            <span style={pillStyle(metrics.transportCounts.Waiter ? "warn" : "default")}>
              Waiters: {metrics.transportCounts.Waiter || 0}
            </span>
            <span style={pillStyle("blue")}>
              Loaners: {metrics.transportCounts.Loaner || 0}
            </span>
            <span style={pillStyle()}>
              Shuttles: {metrics.transportCounts.Shuttle || 0}
            </span>
            <span style={pillStyle()}>
              Other: {metrics.transportCounts.Other || 0}
            </span>
          </div>
        </div>
      </div>

          <div style={card}>
            <div style={sectionTitle}>Next Promise Times</div>

            {metrics.nextPromises.length === 0 ? (
              <EmptyState message="No upcoming promise times for this advisor view." />
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {metrics.nextPromises.map((ro) => (
                  <MiniRO
                    key={ro.id || ro.roNumber}
                    ro={ro}
                    rightLabel={formatDateTime(ro.promiseTimeISO || ro.promiseTime)}
                    danger={isOverdue(ro)}
                  />
                ))}
              </div>
            )}
          </div>

          <div style={card}>
            <div style={sectionTitle}>Recently Completed</div>

            {metrics.recentlyCompleted.length === 0 ? (
              <EmptyState message={`No completed ROs for ${dateWindow.label}.`} />
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {metrics.recentlyCompleted.map((ro) => (
                  <MiniRO
                    key={ro.id || ro.roNumber}
                    ro={ro}
                    rightLabel={formatDateTime(getCompletedAt(ro))}
                    good
                  />
                ))}
              </div>
            )}
          </div>

      <style>
        {`
          @media (max-width: 1180px) {
            div[style*="grid-template-columns: repeat(4"] {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            div[style*="grid-template-columns: repeat(4"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

/* ===============================
   Small Components
================================ */
function SummaryStat({ label, value, style }) {
  return (
    <div
      style={{
        border: "1px solid rgba(15,23,42,0.07)",
        borderRadius: 12,
        padding: "7px 9px",
        background: "#ffffff",
        minWidth: 0,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 10.2,
          color: "rgba(15,23,42,0.44)",
          fontWeight: 850,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: "rgba(15,23,42,0.80)",
          fontSize: 12.2,
          fontWeight: 800,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={safeString(value)}
      >
        {value}
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      style={{
        padding: 10,
        border: "1px dashed #cbd5e1",
        borderRadius: 12,
        color: "rgba(15,23,42,0.55)",
        background: "#f8fafc",
        fontSize: 11.5,
      }}
    >
      {message}
    </div>
  );
}

function MiniRO({ ro, rightLabel, danger = false, good = false }) {
  const tone = danger ? "danger" : good ? "good" : "default";

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 8,
        background: "#ffffff",
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            gap: 7,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <b style={{ color: "rgba(15,23,42,0.94)", fontSize: 13.25, fontWeight: 800 }}>
            RO #{safeString(ro?.roNumber || ro?.ro_number || "—")}
          </b>

          <span style={pillStyle(tone)}>
            {danger ? "Overdue" : good ? "Completed" : "Open"}
          </span>
        </div>

        <div style={{ marginTop: 3, fontSize: 11.5, color: "rgba(15,23,42,0.74)" }}>
          <b>{safeString(ro?.customerName || "—")}</b>
          {" — "}
          {safeString(ro?.vehicle || "—")}
        </div>

        <div style={{ marginTop: 2, fontSize: 10.8, color: "rgba(15,23,42,0.55)" }}>
          Advisor: {advisorDisplayName(ro)}
        </div>
      </div>

      <div
        style={{
          fontSize: 11,
          color: danger ? "#991b1b" : "rgba(15,23,42,0.55)",
          fontWeight: 900,
          whiteSpace: "nowrap",
        }}
      >
        {rightLabel || "—"}
      </div>
    </div>
  );
}
